import { useState, useEffect } from 'react'
import { Box, LinearProgress, Typography, Chip } from '@mui/material'
import { apiClient } from '@shared/services/ApiClient'

interface PasswordStrengthResult {
  score: number
  percentage: number
  level: string
  color: string
  feedback: string[]
}

interface PasswordStrengthMeterProps {
  password: string
  onStrengthChange?: (result: PasswordStrengthResult | null) => void
}

export default function PasswordStrengthMeter({ password, onStrengthChange }: PasswordStrengthMeterProps) {
  const [strength, setStrength] = useState<PasswordStrengthResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    // Debounce the API call
    const timer = setTimeout(() => {
      if (password) {
        checkPasswordStrength(password)
      } else {
        setStrength(null)
        onStrengthChange?.(null)
      }
    }, 300) // Wait 300ms after user stops typing

    return () => clearTimeout(timer)
  }, [password])

  const checkPasswordStrength = async (pwd: string) => {
    try {
      setIsLoading(true)
      const result = await apiClient.post<PasswordStrengthResult>('/auth/check-password-strength', {
        password: pwd
      })

      setStrength(result)
      onStrengthChange?.(result)
    } catch (error) {
      console.error('Error checking password strength:', error)
    } finally {
      setIsLoading(false)
    }
  }

  if (!password || !strength) {
    return null
  }

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      {/* Strength Bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <LinearProgress
            variant="determinate"
            value={strength.percentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: strength.color,
                borderRadius: 4,
                transition: 'all 0.3s ease',
              },
            }}
          />
        </Box>
        <Chip
          label={strength.level}
          size="small"
          sx={{
            backgroundColor: strength.color,
            color: '#fff',
            fontWeight: 600,
            minWidth: 100,
            transition: 'all 0.3s ease',
          }}
        />
      </Box>

      {/* Feedback Messages */}
      {strength.feedback && strength.feedback.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {strength.feedback.map((message, index) => (
            <Typography
              key={index}
              variant="caption"
              sx={{
                display: 'block',
                color: strength.score >= 80 ? strength.color : '#666',
                mb: 0.5,
                fontSize: '0.75rem',
              }}
            >
              {message.includes('🎉') || message.includes('🔒') ? '✓' : '•'} {message}
            </Typography>
          ))}
        </Box>
      )}

      {/* Loading Indicator */}
      {isLoading && (
        <Typography variant="caption" sx={{ display: 'block', color: '#999', mt: 1 }}>
          Checking strength...
        </Typography>
      )}
    </Box>
  )
}
